/**
 * Calendar days ("31 Oct 2026") that have no time and no time zone — a delivery date, a line's start
 * or end date — as they cross between a date picker and the API.
 *
 * A picker works in LOCAL midnight; the API stores a day as UTC midnight (`2026-10-31` →
 * `2026-10-31T00:00:00.000Z`). Converting with `date.toISOString()` or `new Date(iso)` therefore
 * moves the day everywhere except UTC itself: a date picked as 31 Oct was saved as 30 Oct in India
 * and New Zealand, and a saved 31 Oct reopened as 30 Oct in the Americas. These two functions
 * read and write the calendar day itself, so the day a person picks is the day that is stored and
 * the day they see again.
 */

/** The calendar day a picker's `Date` shows, as `YYYY-MM-DD` — its LOCAL year, month and day. */
export function toDateOnly(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * A stored day (`2026-10-31` or `2026-10-31T00:00:00.000Z`) as the local-midnight `Date` a picker
 * expects — the same calendar day, never the same instant.
 */
export function fromDateOnly(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}
